import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table as AntTable } from 'antd';
import DashboardModal from './DashboardModal';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import type { LightreachEligibleProductModalProps } from './LightreachEligibleProductModal.types';
import {
    getLightreachLeadIdFromOpportunity,
    LIGHTREACH_ELIGIBLE_COLUMNS,
    normalizeLightreachPricingRows,
    sortLightreachEligibleRows,
} from './lightreachEligibleProductUtils';

export const LightreachEligibleProductModal: React.FC<LightreachEligibleProductModalProps> = ({
    open,
    onClose,
    theme,
    opportunityDetails,
    noticeApi,
    zIndex = 10070,
}) => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<Record<string, unknown>[]>([]);
    const loadGenRef = useRef(0);

    const opportunityId = useMemo(() => {
        const od = opportunityDetails as { Opportunity_Id?: string | number; OpportunityId?: string | number } | null | undefined;
        return od?.Opportunity_Id ?? od?.OpportunityId ?? null;
    }, [opportunityDetails]);

    const notifClass = `obd-notification obd-theme-${theme}`;

    const loadEligibleProducts = useCallback(async () => {
        const oid = opportunityId;
        if (oid == null || String(oid).trim() === '') {
            noticeApi?.warning({
                message: 'Missing opportunity',
                description: 'Unable to load eligible products without an opportunity id.',
                className: notifClass,
            });
            onClose();
            return;
        }

        const leadId = getLightreachLeadIdFromOpportunity(opportunityDetails);
        if (!leadId) {
            noticeApi?.warning({
                message: 'LightReach lead ID required',
                description: 'Complete LightReach lead sync so a finance partner lead ID is available.',
                className: notifClass,
            });
            onClose();
            return;
        }

        const gen = ++loadGenRef.current;
        setLoading(true);
        setRows([]);
        try {
            const res = await quoteServices.getLightreachPricingByAccount(oid, leadId);
            if (gen !== loadGenRef.current) return;
            setRows(sortLightreachEligibleRows(normalizeLightreachPricingRows(res)));
        } catch (e: unknown) {
            if (gen !== loadGenRef.current) return;
            const responseData = (e as any)?.response?.data;
            const apiMessage = String(responseData?.Message ?? responseData?.message ?? '').trim();
            const isNoRecords = apiMessage.toLowerCase() === 'no records found';

            if (isNoRecords) {
                setRows([]);
            } else {
                const msg = e instanceof Error ? e.message : 'Request failed.';
                noticeApi?.error({
                    message: 'Could not load eligible products',
                    description: msg,
                    className: notifClass,
                });
            }
        } finally {
            if (gen === loadGenRef.current) setLoading(false);
        }
    }, [notifClass, noticeApi, onClose, opportunityDetails, opportunityId]);

    useEffect(() => {
        if (!open) {
            loadGenRef.current += 1;
            setLoading(false);
            setRows([]);
            return;
        }
        void loadEligibleProducts();
    }, [loadEligibleProducts, open]);

    return (
        <DashboardModal
            title="Eligible Products"
            open={open}
            onClose={onClose}
            footer={null}
            width="min(1200px, 98vw)"
            theme={theme}
            maskClosable={false}
            destroyOnClose
            className="obd-dashboard-modal--lr-eligible-products"
            rootClassName={`obd-theme-${theme}`}
            zIndex={zIndex}
        >
            <div className="obd-lr-eligible-products-table">
                {rows.length > 0 && (
                    <AntTable<Record<string, unknown>>
                        size="middle"
                        loading={loading}
                        columns={LIGHTREACH_ELIGIBLE_COLUMNS}
                        dataSource={rows}
                        rowKey={(r, i) =>
                            String(r.ProductId ?? r.Id ?? r.Offer_Id ?? r.offer_Id ?? `lr-elig-${i}`)
                        }
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: false,
                            showTotal: (total, range) =>
                                total > 0 ? `${range[0]}–${range[1]} of ${total}` : '',
                            className: 'obd-lr-eligible-products-pagination',
                        }}
                        scroll={{ x: 'max-content' }}
                    />
                )}
            </div>
        </DashboardModal>
    );
};

export default LightreachEligibleProductModal;
